/**
 * OS ITENS PARADOS DA MANUTENÇÃO (D71, D81, D83). Quem vê é decidido no SERVIDOR (`/api/v1/manutencao/parados`): para os
 * outros o quadro não existe e a Home segue inteira.
 *  · O número é o tamanho da LISTA que ele abre — um número, uma fonte.
 *  · Zero não é botão (não é um lugar para ir): vira texto.
 *  · O clique abre a lista no lugar; cada linha leva à tela que já existe, aberta no item.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Dialog, DialogContent, DialogHeader, DialogTitle, HelpTip } from '@/components/ui';
import { api } from '@/lib/api';

interface ItemParado {
  id: string;
  rotulo: string;
  cliente: string;
  desde: string;
  diasUteis: number;
  para: string;
}
interface NumeroParado {
  chave: string;
  titulo: string;
  explicacao: string;
  itens: ItemParado[];
}

const dia = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;

export function ItensParadosWidget() {
  const [dados, setDados] = useState<{ ve: boolean; numeros: NumeroParado[] } | null>(null);
  const [aberto, setAberto] = useState<NumeroParado | null>(null);

  useEffect(() => {
    api
      .get<{ ve: boolean; numeros: NumeroParado[] }>('/api/v1/manutencao/parados')
      .then(setDados)
      .catch(() => setDados(null));
  }, []);

  if (!dados?.ve) return null;

  return (
    <section className="mb-6" data-testid="itens-parados">
      <h2 className="mb-2 flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-ink-2 dark:text-ink-dark-2">
        Parados na Manutenção <HelpTip texto="Dias úteis do horário de funcionamento de Configurações. Clique no número para ver o que ele conta." />
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {dados.numeros.map((n) => {
          const qtd = n.itens.length;
          const corpo = (
            <>
              <span className="block text-sm text-ink-2 dark:text-ink-dark-2">{n.titulo}</span>
              <span className={`block font-display text-3xl ${qtd ? 'text-red dark:text-red-glow' : 'text-ink-3 dark:text-ink-dark-3'}`}>{qtd}</span>
              <span className="block text-xs text-ink-3 dark:text-ink-dark-3">{n.explicacao}</span>
            </>
          );
          const caixa = 'rounded-lg border border-line bg-paper-surface p-4 text-left dark:border-line-dark dark:bg-paper-dark-surface';
          return qtd ? (
            <button key={n.chave} type="button" onClick={() => setAberto(n)} className={`${caixa} min-h-[44px] transition-colors hover:border-red focus:outline-none focus-visible:ring-2 focus-visible:ring-red-soft`}>
              {corpo}
            </button>
          ) : (
            <div key={n.chave} className={caixa}>
              {corpo}
            </div>
          );
        })}
      </div>

      <Dialog open={aberto !== null} onOpenChange={(o) => !o && setAberto(null)} size="md">
        <DialogHeader>
          <DialogTitle>
            {aberto?.titulo} ({aberto?.itens.length})
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          <p className="mb-3 text-sm text-ink-3 dark:text-ink-dark-3">{aberto?.explicacao}</p>
          <ul className="flex flex-col">
            {aberto?.itens.map((i) => (
              <li key={i.id} className="border-t border-line first:border-t-0 dark:border-line-dark">
                <Link to={i.para} className="flex min-h-[44px] flex-wrap items-center justify-between gap-2 py-2 text-sm hover:text-red">
                  <span>
                    <strong>{i.rotulo}</strong> · {i.cliente}
                  </span>
                  <span className="text-ink-3 dark:text-ink-dark-3">
                    desde {dia(i.desde)} · {i.diasUteis} {i.diasUteis === 1 ? 'dia útil' : 'dias úteis'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </section>
  );
}
